<template lang="pug">
loading(v-if="isLoading" height="75vh")
.competition(v-else)
  .competition-overlays
    roguebuilder-sidebar(:entity="{roguebuilder: roguebuilder}" ref="dynamicSidebar")
    Modal(v-if="overlay === 'alreadyHasPremium'" title="Rogue Premium" @close="uiStore.hideOverlay()")
      .premium-modal
        h2 {{__('Ticket Already Purchased', eventNamespace)}}
        p {{__('You have already purchased access to Iron Game Premium.', eventNamespace)}}
        p(v-if="premiumModalDates" v-html="__('Premium content runs <span>{premiumDates}</span>', {premiumDates: premiumModalDates}, 'auto')")
        RogueButton(v-if="roguebuilder?.props.state === 'live'" color="black" width="full" @click="closePremiumPopup")  {{__('See Premium Content', eventNamespace)}}
    Modal(v-if="overlay === 'premiumTrialStarted'" title="Rogue Premium Trial Started" @close="uiStore.hideOverlay()")
      .premium-modal
        h2 {{__('Premium Trial Started', eventNamespace)}}
        p {{__(`Your Iron Game Premium free trial will last until ${(new Date(premiumTrial.expiration)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`, eventNamespace)}}
        RogueButton(v-if="roguebuilder?.props.state === 'live'" color="black" width="full" @click="closePremiumPopup")  {{__('See Premium Content', eventNamespace)}}
    Modal(v-if="overlay === 'premiumTrialUnavailable'" title="Premium Trial Unavailable" @close="uiStore.hideOverlay()")
      .premium-modal
        h2 {{__('Premium Trial Not Available', eventNamespace)}}
        p {{__('Unable to start Iron Game Premium free trial. This is most likely because you have already used the trial or because it is no longer available.', eventNamespace)}}
        RogueButton(color="black" width="full" @click="purchasePremium()") {{__('Purchase', eventNamespace)}}
    Modal(v-if="overlay === 'buyPremium'" title="Buy Rogue Premium" @close="uiStore.hideOverlay()")
      .premium-modal
        h2 {{__('Iron Game Premium Required', eventNamespace)}}
        p
          | {{__(premiumModalText, 'auto')}}
          strong &nbsp;{{$rogue.currency(product?.price)}}.
        RogueButton(color="black" width="full" @click="purchasePremium()") {{__('Purchase', eventNamespace)}}
        RogueButton.start-trial-button(v-if="premiumTrialAvailable" color="red" width="full" @click="startPremiumTrial()") {{__('Start Free Premium Trial', eventNamespace)}}
    Drawer(v-if="['premiumLogin', 'premiumTrialLogin', 'regularLogin'].includes(overlay)" :title="showRegister ? 'Create an Account' : 'Sign In'")
      DrawerContents
        Message.mbtm(v-if="premiumSku" status="warning" icon="guest" iconColor="black")
          | {{__('To access Iron Game Premium, please log in or create an account.', eventNamespace)}}
        Message.mbtm(v-else status="warning" icon="guest" iconColor="black")
          | {{__('Please log in or create an account.', eventNamespace)}}
        component(
          :is="showRegister ? 'register' : 'login'"
          :successFn="(premiumSku && overlay !== 'regularLogin') ? (overlay === 'premiumLogin' ? purchasePremium : startPremiumTrial) : regularLogin"
          :extended="false"
        )
          .switch-type
            | {{showRegister ? __('Already have an account?') : __('New Customer?')}}
            .link(@click="showRegister = !showRegister") {{ showRegister ? __('Sign In') : __('Create an Account')}}
  .competition-page(@click="checkClick($event)")
    RenderBlock(:block="roguebuilder" :product="{sku: pageId}")
</template>
<script setup lang="ts">
import { onMounted, computed, ref, nextTick, watch, provide, onServerPrefetch, onBeforeUnmount } from 'vue'
import { useHead } from '@unhead/vue'
import { useRoute, useRouter } from 'vue-router'
import { eventNamespace } from 'sisu/js/constants'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import { getCompetitionMetaDescription } from './competitionMetaData'
import metadata, { type MetadataType } from '../../helpers/metadata'
import { useRogue } from '@/js/services/Rogue'
import { messages } from '@/js/app-bridge/messages'
import { useCmsStore } from '@/js/stores/cmsStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { useUIStore } from '@/js/stores/uiStore'
import { createBuyRequest } from '@/js/helpers/buy-request'
import RoguebuilderSidebar from '@/js/components/roguebuilder-sidebar.vue'
import type { Product } from '@/js/services/RogueTypes'

const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const cmsStore = useCmsStore()
const customerStore = useCustomerStore()
const listStore = useListStore()
const productStore = useProductStore()
const uiStore = useUIStore()

const showRegister = ref(false)
const product = ref<Product | undefined>()
 
let timer: NodeJS.Timeout | string = ''

const overlay = computed(() => uiStore.overlay)
const products = computed(() => productStore.products)
const loggedIn = computed(() => customerStore.loggedIn)
const tickets = computed(() => customerStore.tickets)
const byIdentifier = computed(() => cmsStore.byIdentifier)
const cms = computed(() => cmsStore.cms)
const pageId = computed(() => {
  const routeParams = route.params
  const path = route.path.toLowerCase()

  // defaulting to invitational to prevent breaking the invitational page for 2023 
  let name = 'invitational'
  let year = '2026'

  if (routeParams?.name && routeParams?.year) {
    name = routeParams.name as string
    year = routeParams.year as string
  }
  else if (route.path.includes('arnold-strongman-classic')){
    const pathYearMatch = path.match(/\d{4}/)
    if (pathYearMatch && !route.params?.year) {
      year = pathYearMatch[0]
    }
    name = 'arnold-strongman-classic'
  }

  return `${name}-${year}`
})

provide('tickets', computed(() => tickets.value))

const dynamicSidebar = ref<InstanceType<typeof RoguebuilderSidebar> | undefined>()

const roguebuilder = computed(() => {
  const data = cms.value[byIdentifier.value[pageId.value]]

  if (data && route.path.indexOf('/invitational') === 0) {
    return {
      ...data.roguebuilder,
      props: {
        ...data.roguebuilder.props,
        baseUrl: '/invitational',
      },
    }
  }
  else if (data && route.path.indexOf('/arnold-strongman-classic') === 0) {
    return {
      ...data.roguebuilder,
      props: {
        ...data.roguebuilder.props,
        baseUrl: '/arnold-strongman-classic',
      },
    }
  }

  return data?.roguebuilder
})

const ogImageSource = computed(() => roguebuilder.value?.props?.ogImage?.src)
const ogImageAlt = computed(() => roguebuilder.value?.props?.ogImage?.alt)
const competitionOgImage = computed(() => getCloudinaryUrl(ogImageSource.value, 1200, 630))

const isLoading = computed(() => !roguebuilder.value)
const premiumSku = computed(() => roguebuilder.value?.props?.premiumSku)
const premiumTrialId = computed(() => roguebuilder.value?.props?.premiumTrialId)
const premiumModalText = computed(() => roguebuilder.value?.props?.premiumModalText)
const premiumModalDates = computed(() => roguebuilder.value?.props?.premiumModalDates)
const premiumTrialAvailable = computed(() => {
  const trialAlreadyUsed = tickets.value.some(t => t.ticketIdentifier === premiumTrialId.value)

  return !!premiumTrialId.value && !trialAlreadyUsed && roguebuilder.value?.props.state === 'live' && !rogue.appContext
})
const premiumTrial = computed(() => tickets.value.find(t => t.ticketIdentifier === premiumTrialId.value))

// You can go to the current year's invitational event by /invitational OR /events/invitational/[year]/[page-path]
// This function creates a canonical URL to be /invitational for the current year even if the user visits /events/invitational/[current-year]/[page-path]
// @TODO - change hardcoded event year to a dynamic year after the 2024 invitational is over
function createCanonicalURL() {
  const urlPath = route.path
  const year = roguebuilder.value?.props?.compYear || new Date().getFullYear()

  return `${rogue.localizedBaseURL}${urlPath}`
    .replace(`/events/invitational/${year}`, '/invitational')
    .replace(`/events/arnold-strongman-classic/${year}`, '/arnold-strongman-classic')
}

const toProper = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)
// Using the path name for the tab title
const pathName = computed(() => route?.params?.catchAll as string)
const currentPage = computed(() => roguebuilder.value?.props.links.find((l: {path: string}) => l.path === pathName.value))

const formatedPathName = computed(() => {
  let name = pathName.value ? toProper(pathName.value) : ''

  if (name.indexOf('-') >= 0) {
    const pathParts = name.split('-')

    pathParts.forEach((p: string, i: number) => pathParts[i] = toProper(p))

    name = pathParts.join(' ')
  }

  if (name === 'Qualifyingsystem') {
    name = 'Qualifying System'
  } else if (name === 'Recordbreakers') {
    name = 'Record Breakers'
  } else if (name === 'Prizepurse') {
    name = 'Prize Purse'
  }

  return name
})

const url = route.path

const isInvitational = url.indexOf('/invitational') >= 0
const titlePrefix = isInvitational ? 'Rogue Invitational' : 'Arnold Strongman Classic'

const title = computed(() => rogue.__(`${titlePrefix} ${currentPage.value?.title || formatedPathName.value} ${roguebuilder.value?.props?.compYear}`))

const metaDescription = computed(() => rogue.__(currentPage.value?.metaDescription || getCompetitionMetaDescription(isInvitational ? 'invitational' : 'arnold', roguebuilder.value?.props?.state || 'upcoming', pathName.value?.toLowerCase() || 'index')?.replace('{year}', roguebuilder.value?.props?.compYear)))

const head = computed<MetadataType>(() => ({
  robots      : 'all',
  title       : title.value,
  url         : createCanonicalURL(),
  description : metaDescription.value,
  image       : competitionOgImage.value,
  imageWidth  : competitionOgImage.value ? 1200 : undefined,
  imageHeight : competitionOgImage.value ? 630 : undefined,
  imageAlt    : competitionOgImage.value ? ogImageAlt.value : undefined,
}))

const headControl = useHead(metadata(head.value, rogue))

function updateHead() {
  headControl.patch(metadata(head.value, rogue))
}

watch(title, () => {
  load()
})


watch(loggedIn, () => {
  load()
})

watch(() => roguebuilder.value?.props?.premiumSku, () => {
  loadPremium()
})

watch(overlay, (newVal) => {
  if (newVal === 'buyPremium' && loggedIn.value && tickets.value.find(t => t.ticketIdentifier === roguebuilder.value?.props?.compId || t.ticketIdentifier === `${roguebuilder.value?.props?.compId}-AU`)) {
    uiStore.showOverlay('alreadyHasPremium', rogue)
  }
})

onServerPrefetch(async () => {
  await load()
})

if (!rogue.ssrContext) {
  load()
}

onMounted(() => {
  timer = setInterval(() => load(true), 300000)

  if (route.hash) {
    setTimeout(() => {
      dynamicSidebar.value?.show(route.hash.substring(1))
    }, 100)
  }

  if (route.query.startPremiumTrial) {
    startPremiumTrial()
  }
})

onBeforeUnmount(() => {
  clearInterval(timer)
  headControl.dispose()
})

function checkClick(e: Event) {
  dynamicSidebar.value?.checkClick(e)
}

async function load(force = false) {
  if (force) {
    cmsStore.forceReload(pageId.value)
  }
  await cmsStore.load(pageId.value, rogue)
  if (!roguebuilder.value) {
    rogue.log.warn(`Error loading event ${pageId.value} ${route.path}`)
    router.replace('/')

    return
  }

  uiStore.eventNavLinks = roguebuilder.value.props?.links
    ?.filter((link: any) => link.active !== false)
    .map((link: any) => ({
      name : link.text,
      url  : `${roguebuilder.value.props?.baseUrl}/${link.path}`.toLowerCase(),
    })) || []
  uiStore.eventBaseUrl = roguebuilder.value.props?.baseUrl
  uiStore.eventLogo = roguebuilder.value.props?.logo

  if (premiumSku.value) {
    await loadTickets()
    await loadPremium()
  }

  if (rogue.i18n.enabled) {
    const loadTranslations = rogue.i18n.loadNamespace(pageId.value)
    if (loadTranslations) {
      await loadTranslations
    }
  }

  updateHead()
}

async function loadPremium() {
  if (premiumSku.value) {
    await productStore.load(premiumSku.value, rogue)
    product.value = Object.values(products.value).find(p => p.sku === premiumSku.value)
  }
}

async function loadTickets() {
  if (!loggedIn.value) {
    return false
  }

  const { compId } = roguebuilder.value?.props || {}

  if (!premiumSku.value || !compId) {
    return false
  }

  // Add the ticket data to the store
  try {
    const resp = await rogue.fetch('/svc_ticket/tickets')
    customerStore.tickets = resp

    return (resp || []).some((t: {ticketIdentifier: string}) => t.ticketIdentifier === compId || t.ticketIdentifier === `${compId}-AU`)
  } catch {
    return false
  }
}

async function purchasePremium() {
  const buyRequest = createBuyRequest(product.value!, rogue)

  if (!premiumSku.value) {
    return
  }

  if (!loggedIn.value) {
    if (!rogue.appContext) {
      uiStore.showOverlay('premiumLogin', rogue)
    } else {
      await listStore.addListItems(rogue, 'buyNow', [buyRequest])
      uiStore.hideOverlay()
      rogue.emitToApp(messages.LOGIN, { redirect: 'checkout/payment' })
    }

    return
  }

  const hasPremium = await loadTickets()

  if (hasPremium) {
    uiStore.showOverlay('alreadyHasPremium', rogue)

    return
  }

  await listStore.addListItems(rogue, 'buyNow', [buyRequest])

  uiStore.hideOverlay()

  nextTick(() => router.push('/checkout/payment'))
}

async function startPremiumTrial() {
  if (!premiumSku.value || !premiumTrialAvailable.value) {
    uiStore.showOverlay('premiumTrialUnavailable', rogue)

    return
  }

  if (!loggedIn.value) {
    if (!rogue.appContext) {
      uiStore.showOverlay('premiumTrialLogin', rogue)
    } else {
      uiStore.hideOverlay()
      rogue.emitToApp(messages.LOGIN, { redirect: `${route.fullPath}?startPremiumTrial=1` })
    }

    return
  }

  try {
    await rogue.fetch(`/svc_ticket/try/${premiumTrialId.value}`, 'POST')
  } catch (e) {
    uiStore.showOverlay('premiumTrialUnavailable', rogue)
    rogue.log.error('Error in API call to start premium trial.', e, {
      identifier : premiumTrialId.value,
      customerId : customerStore._id,
    })

    return
  }

  await loadTickets()

  if (route.path.includes('/premium')) {
    uiStore.showOverlay('premiumTrialStarted', rogue)
  } else {
    uiStore.hideOverlay()
  }
}

function regularLogin() {
  uiStore.hideOverlay()
  rogue.emitToApp(messages.LOGIN)
}

function closePremiumPopup() {
  uiStore.hideOverlay()
  router.push('/invitational/live')
}
</script>
<style lang="scss" scoped>
.competition{
  min-height: 75vh;
}
.premium-modal {
  text-align: center;
  padding: 0 $space-m;

  p {
    margin-bottom: $space-m;
  }
}

.competition-overlays {
  z-index: 100;
  position: relative;
}

.already-registered {
  margin: $space-l 0 $space-s 0;
}

.msg {
  margin: $space-s 0;
}

.mbtm {
  margin-bottom: $space-s;
}

.switch-type {
  font-size: $size-xs;
  text-align: center;
  margin-top: $space-s;

  .link {
    display: inline-block;
    margin-left: $space-xxs;
  }
}

.start-trial-button {
  margin-top: $space-s;
}
</style>
